import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
} from "react";
import { Toast } from "primereact/toast";

import type { ToastMessage } from "primereact/toast";
import type { RefObject } from "react";

type NetworkContextType = {
  isOnline: boolean;
  toastRef: RefObject<Toast | null>;
  showToast: (message: ToastMessage) => void;
  showApiError: (title: string, message: string, sticky: boolean) => void;
  showNetworkError: (title: string, message: string, sticky: boolean) => void;
};

const NetworkContext = createContext<NetworkContextType | null>(null);

export const NetworkProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const toastRef = useRef<Toast>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Generic toast
  const showToast = (message: ToastMessage) => {
    toastRef.current?.show(message);
  };

  // API Error
  const showApiError = (
    title: string,
    message: string,
    sticky: boolean = false,
  ) => {
    toastRef.current?.show({
      severity: "error",
      summary: title,
      detail: message,
      sticky: sticky,
      ...(sticky ? {} : { life: 5000 }),
    });
  };

  // save attempt while offline
  const showNetworkError = (
    title: string,
    message: string,
    sticky: boolean = false,
  ) => {
    toastRef.current?.show({
      severity: "error",
      summary: title,
      detail: message,
      sticky: sticky,
      closable: false,
    });
  };

  // automatic network outage detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // toastRef.current?.clear();
    };

    const handleOffline = () => {
      setIsOnline(false);
      toastRef.current?.show({
        severity: "error",
        summary: "Network Connection Lost",
        detail:
          "It looks like you're not connected to the internet. Changes you make may not be saved. Please reconnect before continuing.",
        sticky: true,
        closable: false,
      });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return (
    <NetworkContext.Provider
      value={{ isOnline, toastRef, showToast, showApiError, showNetworkError }}
    >
      {children}
    </NetworkContext.Provider>
  );
};

export const useNetwork = () => {
  const context = useContext(NetworkContext);
  if (!context) {
    throw new Error("useNetwork must be used within NetworkProvider");
  }
  return context;
};
